import axios from 'axios';
import { AudioRecorder } from '@apia/util';
import { ApiaApi, makeApiaUrl, parseSuccessfulResponse } from '@apia/api';
import { ResponseStream } from '../stream/ResponseStream';
import type { AssistantStreamEvent } from '../stream/ResponseStream';
import { action, makeObservable, observable } from 'mobx';

type RoutinesGenerationState = {
  isLoading: boolean;
  progress: number;
};

type TPollProgress = {
  message: string;
  progress: number;
  debug: string;
  streamEvents?: AssistantStreamEvent[];
  streamPart?: string;
};

export type TPollRoutineResult = {
  status: string;
  payload: unknown | TPollProgress;
};

export type TGenerateFromImage = {
  base64: string;
  description: string;
};

export type TRoutineCompletion = Partial<{
  images: TGenerateFromImage[];
  parameters: Record<string, string>;
  stream: ResponseStream;
}> & {
  routineName: string;
};

function isPollProgress(
  payload: unknown | TPollProgress,
): payload is TPollProgress {
  return (payload as TPollProgress)?.progress !== undefined;
}

export class RoutinesGeneration {
  public audioRecorder = new AudioRecorder();
  private routineId: string = '';
  public state: RoutinesGenerationState = {
    isLoading: false,
    progress: 0,
  };

  public constructor() {
    makeObservable(this, {
      completion: action,
      state: observable,
      stop: action,
      transcribeAudio: action,
    });
  }

  private checkCanGenerate() {
    if (this.state.isLoading) {
      throw new Error('Cannot queue two simultaneous generations');
    }

    this.state.isLoading = true;
  }

  public async pollRoutine<T>(routineId: string, stream?: ResponseStream) {
    this.routineId = routineId;
    this.state.isLoading = true;
    return this.internalPollRoutine<T>(stream);
  }

  private async internalPollRoutine<T>(stream?: ResponseStream) {
    while (this.state.isLoading) {
      const pollResult = await this.callRoutinePoll(this.routineId);
      if (isPollProgress(pollResult.payload)) {
        try {
          const res = JSON.parse(pollResult.payload.debug) as string[];
          if (res.length > 0) {
            res.forEach((c) => {
              console.log(JSON.parse(c));
            });
          }

          if (stream) {
            if (Array.isArray(pollResult.payload.streamEvents)) {
              stream.emitAssistantStreamEvents(pollResult.payload.streamEvents);
            } else if (pollResult.payload.streamPart) {
              stream.emit('part', pollResult.payload.streamPart);
            }
          }
          this.state.progress = pollResult.payload.progress;
        } catch (e) {
          console.error(e);
        }
      } else if (pollResult.status === 'FINISHED') {
        this.resetLoading();
        return pollResult.payload as T;
      } else if (pollResult.status === 'ERROR') {
        this.resetLoading();
        throw new Error(pollResult.payload as string);
      } else {
        this.resetLoading();
        throw new Error('Wrong poll response');
      }
    }

    return null;
  }

  protected async callAudioTranscription(
    audio: Blob,
  ): ReturnType<RoutinesGeneration['callRoutineStart']> {
    const url = makeApiaUrl({
      action: 'routineStart',
      routine: 'transcription/Transcribe',
    });

    const response = await axios.post(url, audio.arrayBuffer(), {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });

    const result = await parseSuccessfulResponse<{
      orchestrator: { routineId: string };
    }>(response, '');

    if (result?.orchestrator) {
      return result;
    }

    return null;
  }

  protected async callRoutinePoll(
    routineId: string,
  ): Promise<TPollRoutineResult> {
    const result = await ApiaApi.post<{ orchestrator: string }>(
      makeApiaUrl({
        action: 'routinePoll',
        routineId,
      }),
    );

    if (result?.data?.orchestrator) {
      return JSON.parse(result.data.orchestrator) as TPollRoutineResult;
    } else {
      throw new Error('Poll failed');
    }
  }

  protected async callRoutineStart(
    routineName: string,
    parameters: Record<string, string>,
    images: TGenerateFromImage[],
  ): Promise<{ orchestrator: { routineId: string } } | null> {
    const result = await ApiaApi.post<{
      orchestrator: { routineId: string };
    }>(
      makeApiaUrl({
        action: 'routineStart',
        routine: routineName,
      }),
      {
        postData: {
          images: JSON.stringify(images),
          ...parameters,
        },
        stringifyOptions: {
          arrayFormat: 'repeat',
        },
        postDataTreatement: 'stringify',
      },
    );

    return result?.data || null;
  }

  private async executeRoutine<T>(
    routineName: string,
    parameters: Record<string, string>,
    images: TGenerateFromImage[],
    stream?: ResponseStream,
  ): Promise<T | null> {
    this.checkCanGenerate();

    try {
      const result = await this.callRoutineStart(
        routineName,
        parameters,
        images,
      );

      if (result?.orchestrator?.routineId !== undefined) {
        this.routineId = result?.orchestrator.routineId;

        return this.internalPollRoutine<T>(stream);
      } else {
        throw new Error('Cannot get routine id');
      }
    } catch (e) {
      this.resetLoading();
      throw e;
    }
  }

  private resetLoading() {
    this.state.isLoading = false;
    this.state.progress = 0;
  }

  public async completion<T>(params: TRoutineCompletion) {
    return this.executeRoutine<T>(
      params.routineName,
      params.parameters ?? {},
      params.images ?? [],
      params.stream,
    );
  }

  public stop() {
    this.resetLoading();
    ApiaApi.get<{
      orchestrator: { routineId: string };
    }>(
      makeApiaUrl({
        action: 'routineStop',
        routineId: this.routineId,
      }),
    );
  }

  public async transcribeAudio(audio: Blob) {
    const result = await this.callAudioTranscription(audio);

    if (result) {
      this.state.isLoading = true;
      if (result?.orchestrator?.routineId) {
        this.routineId = result?.orchestrator?.routineId;
        return await this.internalPollRoutine<{
          transcription: string;
        }>();
      }
    }

    throw new Error('Something went wrong while transcribing the audio');
  }
}
